import {URLS, RESPONSES} from "@/constants";


export default {
    namespaced: true,
    state: {
        companyInfo: null,
        staff: [],
        services: [],
        slots: [],
        selectedShop: null,
        selectedStaff: null,
        selectedService: null,
        selectedDate: null,
        selectedSlot: null,
        confirmation: null,
        step: 1,
        isLoading: false,
        error: null,
    },
    mutations: {
        setCompanyInfo(state, value) { state.companyInfo = value; },
        setStaff(state, value) { state.staff = value; },
        setServices(state, value) { state.services = value; },
        setSlots(state, value) { state.slots = value; },
        setSelectedShop(state, value) { state.selectedShop = value; },
        setSelectedStaff(state, value) { state.selectedStaff = value; },
        setSelectedService(state, value) { state.selectedService = value; },
        setSelectedDate(state, value) { state.selectedDate = value; },
        setSelectedSlot(state, value) { state.selectedSlot = value; },
        setConfirmation(state, value) { state.confirmation = value; },
        setStep(state, value) { state.step = value; },
        setLoading(state, value) { state.isLoading = value; },
        setError(state, value) { state.error = value; },
        resetWizard(state) {
            state.staff = [];
            state.services = [];
            state.slots = [];
            state.selectedShop = null;
            state.selectedStaff = null;
            state.selectedService = null;
            state.selectedDate = null;
            state.selectedSlot = null;
            state.confirmation = null;
            state.step = 1;
            state.error = null;
        },
    },
    actions: {
        async _fetchList({commit}, {url, mutation, errorMutation}) {
            commit("setLoading", true);
            try {
                const response = await fetch(url, {method: "GET"});
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    commit(mutation, data.data);
                    return {successfully: true};
                }
                if (errorMutation) commit(errorMutation, "load_failed");
                return {successfully: false};
            } catch {
                if (errorMutation) commit(errorMutation, "network_error");
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },

        async fetchInfo({commit}, token) {
            commit("setLoading", true);
            commit("setError", null);
            const url = URLS.PUBLIC_BOOKING.INFO_URL(token);
            const options = {method: "GET"};
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    commit("setCompanyInfo", data.data);
                    return {successfully: true};
                }
                commit("setError", data.detail || "not_available");
                return {successfully: false};
            } catch {
                commit("setError", "network_error");
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },

        async fetchStaff({dispatch}, {token, shopId}) {
            const params = new URLSearchParams();
            if (shopId) params.append("shop_id", shopId);
            return dispatch("_fetchList", {
                url: `${URLS.PUBLIC_BOOKING.STAFF_URL(token)}?${params.toString()}`,
                mutation: "setStaff",
                errorMutation: "setError",
            });
        },

        async fetchServices({dispatch}, {token, shopId}) {
            const params = new URLSearchParams();
            if (shopId) params.append("shop_id", shopId);
            return dispatch("_fetchList", {
                url: `${URLS.PUBLIC_BOOKING.SERVICES_URL(token)}?${params.toString()}`,
                mutation: "setServices",
                errorMutation: "setError",
            });
        },

        async fetchSlots({commit}, {token, shopId, staffId, serviceId, date}) {
            commit("setLoading", true);
            const params = new URLSearchParams();
            if (shopId) params.append("shop_id", shopId);
            if (staffId) params.append("staff_id", staffId);
            if (serviceId) params.append("service_id", serviceId);
            if (date) params.append("date", date);
            const url = `${URLS.PUBLIC_BOOKING.SLOTS_URL(token)}?${params.toString()}`;
            const options = {method: "GET"};
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    commit("setSlots", data.data);
                    return {successfully: true};
                }
                commit("setSlots", []);
                commit("setError", "load_failed");
                return {successfully: false};
            } catch {
                commit("setSlots", []);
                commit("setError", "network_error");
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },

        async createBooking({commit}, {token, payload}) {
            commit("setLoading", true);
            const url = URLS.PUBLIC_BOOKING.BOOK_URL(token);
            const options = {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(payload),
            };
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    commit("setConfirmation", data.data);
                    return {successfully: true};
                }
                return {successfully: false, detail: data.detail};
            } catch {
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },

        async fetchCancelInfo({commit}, {token, cancelToken}) {
            commit("setLoading", true);
            commit("setError", null);
            const url = URLS.PUBLIC_BOOKING.CANCEL_URL(token, cancelToken);
            const options = {method: "GET"};
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    commit("setConfirmation", data.data);
                    return {successfully: true};
                }
                commit("setError", data.detail || "not_found");
                return {successfully: false};
            } catch {
                commit("setError", "network_error");
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },

        async confirmCancel({commit}, {token, cancelToken}) {
            commit("setLoading", true);
            const url = URLS.PUBLIC_BOOKING.CANCEL_URL(token, cancelToken);
            const options = {
                method: "POST",
                headers: {"Content-Type": "application/json"},
            };
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    return {successfully: true};
                }
                return {successfully: false, detail: data.detail};
            } catch {
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },
        async confirmBooking({commit}, {token, confirmToken}) {
            commit("setLoading", true);
            const url = URLS.PUBLIC_BOOKING.CONFIRM_URL(token, confirmToken);
            const options = {
                method: "POST",
                headers: {"Content-Type": "application/json"},
            };
            try {
                const response = await fetch(url, options);
                const data = await response.json();
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    return {successfully: true, data: data.data};
                }
                return {successfully: false, detail: data.detail};
            } catch {
                return {successfully: false};
            } finally {
                commit("setLoading", false);
            }
        },
    },
};
